<template>
<div>
    <!-- HERO -->
    <section class="pt-12 pt-md-14 mt-n11 bg-primary">
        <div class="container">
            <div class="row align-items-center">
                <div class="col-12 col-md-8">
                    <nav aria-label="breadcrumb">
                        <ol class="breadcrumb breadcrumb-dark mb-2">
                            <li class="breadcrumb-item"><router-link to="/docs" class="text-white-80">Docs</router-link></li>
                            <li class="breadcrumb-item active text-white" aria-current="page">Datasets</li>
                        </ol>
                    </nav>
                    <h1 class="display-3 fw-bold text-white">Datasets Documentation</h1>
                    <p class="lead text-white-80 mb-0">
                        Pre-built Instagram datasets with millions of profiles and posts ready for download.
                    </p>
                </div>
            </div>
        </div>
    </section>

    <!-- CONTENT -->
    <section class="py-8 py-md-11">
        <div class="container">
            <div class="row">
                <!-- SIDEBAR NAV -->
                <div class="col-12 col-lg-3 mb-8 mb-lg-0">
                    <div class="docs-nav-sidebar">
                        <h6 class="text-uppercase text-muted mb-3">On this page</h6>
                        <nav class="nav flex-column">
                            <a href="#overview" class="nav-link px-0">Overview</a>
                            <a href="#available" class="nav-link px-0">Available Datasets</a>
                            <a href="#purchase" class="nav-link px-0">How to Purchase</a>
                            <a href="#format" class="nav-link px-0">Data Format</a>
                            <a href="#use-cases" class="nav-link px-0">Use Cases</a>
                        </nav>
                    </div>
                </div>

                <!-- MAIN CONTENT -->
                <div class="col-12 col-lg-9">
                    <!-- OVERVIEW -->
                    <div id="overview" class="docs-section">
                        <h2 class="fw-bold">Overview</h2>
                        <p class="text-muted fs-lg">
                            Our pre-built datasets contain millions of Instagram profiles and posts extracted from
                            our database. These datasets are ideal for market research, machine learning,
                            and data analysis projects.
                        </p>
                        <div class="row mt-4">
                            <div class="col-12 col-md-4 mb-3">
                                <div class="text-center p-4 bg-dark rounded">
                                    <h3 class="text-primary fw-bold mb-0">975M+</h3>
                                    <small class="text-muted">User profiles</small>
                                </div>
                            </div>
                            <div class="col-12 col-md-4 mb-3">
                                <div class="text-center p-4 bg-dark rounded">
                                    <h3 class="text-primary fw-bold mb-0">192M+</h3>
                                    <small class="text-muted">Posts & reels</small>
                                </div>
                            </div>
                            <div class="col-12 col-md-4 mb-3">
                                <div class="text-center p-4 bg-dark rounded">
                                    <h3 class="text-primary fw-bold mb-0">CSV</h3>
                                    <small class="text-muted">Export format</small>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- AVAILABLE DATASETS -->
                    <div id="available" class="docs-section">
                        <h2 class="fw-bold">Available Datasets</h2>
                        <p class="text-muted mb-4">
                            We offer datasets in various sizes to fit your needs and budget:
                        </p>

                        <h5 class="mt-4 mb-3">Instagram Users</h5>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Dataset</th>
                                        <th>Rows</th>
                                        <th>Fields</th>
                                        <th>Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Instagram Users - Sample</td>
                                        <td>10,000</td>
                                        <td>50+</td>
                                        <td>$5</td>
                                    </tr>
                                    <tr>
                                        <td>Instagram Users - Small</td>
                                        <td>100,000</td>
                                        <td>50+</td>
                                        <td>$25</td>
                                    </tr>
                                    <tr>
                                        <td>Instagram Users - Medium</td>
                                        <td>1,000,000</td>
                                        <td>50+</td>
                                        <td>$150</td>
                                    </tr>
                                    <tr>
                                        <td>Instagram Users - Large</td>
                                        <td>10,000,000</td>
                                        <td>50+</td>
                                        <td>$500</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h5 class="mt-5 mb-3">Instagram Posts</h5>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Dataset</th>
                                        <th>Rows</th>
                                        <th>Fields</th>
                                        <th>Price</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Instagram Posts - Sample</td>
                                        <td>10,000</td>
                                        <td>40+</td>
                                        <td>$5</td>
                                    </tr>
                                    <tr>
                                        <td>Instagram Posts - Small</td>
                                        <td>100,000</td>
                                        <td>40+</td>
                                        <td>$20</td>
                                    </tr>
                                    <tr>
                                        <td>Instagram Posts - Medium</td>
                                        <td>1,000,000</td>
                                        <td>40+</td>
                                        <td>$100</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="alert alert-primary mt-4">
                            <strong>View all datasets:</strong>
                            <router-link :to="{path: '/datasets'}">Browse our catalog</router-link> for the complete list with current prices.
                        </div>
                    </div>

                    <!-- PURCHASE FLOW -->
                    <div id="purchase" class="docs-section">
                        <h2 class="fw-bold">How to Purchase</h2>
                        <p class="text-muted mb-4">
                            Purchasing a dataset is simple and takes just a few minutes:
                        </p>

                        <div class="docs-diagram">
                            <pre class="mb-0 text-white-80">
┌───────────────────────────────────────────────────────────────────────────┐
│                           Purchase Flow                                    │
└───────────────────────────────────────────────────────────────────────────┘

  ┌─────────┐      ┌─────────┐      ┌─────────┐      ┌─────────┐
  │ 1.Browse│─────▶│ 2.Select│─────▶│ 3.Pay   │─────▶│4.Download│
  │ Catalog │      │   Size  │      │         │      │   CSV   │
  └─────────┘      └─────────┘      └─────────┘      └─────────┘

  /datasets        Click "Buy"      Use balance      Instant download
                   Choose size      or top up        after generation</pre>
                        </div>

                        <ol class="text-muted mt-4">
                            <li class="mb-3">
                                <strong class="text-white">Browse the catalog</strong><br>
                                Go to <router-link :to="{path: '/datasets'}">Datasets</router-link> page and explore available datasets
                            </li>
                            <li class="mb-3">
                                <strong class="text-white">Select size and click Buy</strong><br>
                                Choose the dataset size that fits your needs. Click "Buy" to open the purchase modal.
                            </li>
                            <li class="mb-3">
                                <strong class="text-white">Confirm payment</strong><br>
                                The cost will be deducted from your account balance. If insufficient, top up first.
                            </li>
                            <li class="mb-3">
                                <strong class="text-white">Download your dataset</strong><br>
                                Once generated (usually instant), download the CSV file from "My Datasets" section.
                            </li>
                        </ol>

                        <div class="card bg-dark border-0 mt-4">
                            <div class="card-body">
                                <h6 class="text-white">Need a specific dataset?</h6>
                                <p class="text-muted small mb-3">
                                    Use Dataset Builder to create custom datasets with your own filters and parameters.
                                </p>
                                <router-link :to="{path: '/docs/dataset-builder'}" class="btn btn-sm btn-primary">
                                    Learn about Dataset Builder <i class="fe fe-arrow-right ms-2"></i>
                                </router-link>
                            </div>
                        </div>
                    </div>

                    <!-- DATA FORMAT -->
                    <div id="format" class="docs-section">
                        <h2 class="fw-bold">Data Format</h2>
                        <p class="text-muted">
                            All datasets are delivered as CSV files with UTF-8 encoding.
                        </p>

                        <h5 class="mt-4 mb-3">User Profile Fields</h5>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Field</th>
                                        <th>Type</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>pk</code></td>
                                        <td>Integer</td>
                                        <td>Instagram user ID</td>
                                    </tr>
                                    <tr>
                                        <td><code>username</code></td>
                                        <td>String</td>
                                        <td>Instagram handle</td>
                                    </tr>
                                    <tr>
                                        <td><code>full_name</code></td>
                                        <td>String</td>
                                        <td>Display name</td>
                                    </tr>
                                    <tr>
                                        <td><code>biography</code></td>
                                        <td>String</td>
                                        <td>Profile bio text</td>
                                    </tr>
                                    <tr>
                                        <td><code>follower_count</code></td>
                                        <td>Integer</td>
                                        <td>Number of followers</td>
                                    </tr>
                                    <tr>
                                        <td><code>following_count</code></td>
                                        <td>Integer</td>
                                        <td>Number following</td>
                                    </tr>
                                    <tr>
                                        <td><code>media_count</code></td>
                                        <td>Integer</td>
                                        <td>Total posts count</td>
                                    </tr>
                                    <tr>
                                        <td><code>is_verified</code></td>
                                        <td>Boolean</td>
                                        <td>Blue checkmark status</td>
                                    </tr>
                                    <tr>
                                        <td><code>is_business</code></td>
                                        <td>Boolean</td>
                                        <td>Business account flag</td>
                                    </tr>
                                    <tr>
                                        <td><code>category</code></td>
                                        <td>String</td>
                                        <td>Business category</td>
                                    </tr>
                                    <tr>
                                        <td><code>external_url</code></td>
                                        <td>String</td>
                                        <td>Website link</td>
                                    </tr>
                                    <tr>
                                        <td><code>public_email</code></td>
                                        <td>String</td>
                                        <td>Contact email (if public)</td>
                                    </tr>
                                    <tr>
                                        <td><code>contact_phone_number</code></td>
                                        <td>String</td>
                                        <td>Phone (if public)</td>
                                    </tr>
                                    <tr>
                                        <td><code>city_name</code></td>
                                        <td>String</td>
                                        <td>Location city</td>
                                    </tr>
                                    <tr>
                                        <td><code>profile_pic_url</code></td>
                                        <td>String</td>
                                        <td>Profile picture URL</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h5 class="mt-5 mb-3">Post Fields</h5>
                        <div class="table-responsive">
                            <table class="table table-dark docs-table">
                                <thead>
                                    <tr>
                                        <th>Field</th>
                                        <th>Type</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>pk</code></td>
                                        <td>Integer</td>
                                        <td>Media ID</td>
                                    </tr>
                                    <tr>
                                        <td><code>shortcode</code></td>
                                        <td>String</td>
                                        <td>Post shortcode (URL slug)</td>
                                    </tr>
                                    <tr>
                                        <td><code>user_pk</code></td>
                                        <td>Integer</td>
                                        <td>Author user ID</td>
                                    </tr>
                                    <tr>
                                        <td><code>caption_text</code></td>
                                        <td>String</td>
                                        <td>Post caption</td>
                                    </tr>
                                    <tr>
                                        <td><code>like_count</code></td>
                                        <td>Integer</td>
                                        <td>Number of likes</td>
                                    </tr>
                                    <tr>
                                        <td><code>comment_count</code></td>
                                        <td>Integer</td>
                                        <td>Number of comments</td>
                                    </tr>
                                    <tr>
                                        <td><code>media_type</code></td>
                                        <td>Integer</td>
                                        <td>1=Photo, 2=Video, 8=Carousel</td>
                                    </tr>
                                    <tr>
                                        <td><code>taken_at</code></td>
                                        <td>DateTime</td>
                                        <td>Post timestamp</td>
                                    </tr>
                                    <tr>
                                        <td><code>thumbnail_url</code></td>
                                        <td>String</td>
                                        <td>Thumbnail image URL</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="alert alert-warning mt-4">
                            <strong>Note:</strong> Some fields may be empty if the user hasn't made them public on Instagram.
                        </div>
                    </div>

                    <!-- USE CASES -->
                    <div id="use-cases" class="docs-section">
                        <h2 class="fw-bold">Use Cases</h2>

                        <div class="row mt-4">
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h5 class="text-white">Market Research</h5>
                                        <p class="text-muted small mb-0">
                                            Analyze competitors, identify market trends, and understand audience demographics
                                            in your industry.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h5 class="text-white">Influencer Discovery</h5>
                                        <p class="text-muted small mb-0">
                                            Find influencers in your niche by filtering on followers, engagement,
                                            and content categories.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h5 class="text-white">Machine Learning</h5>
                                        <p class="text-muted small mb-0">
                                            Train ML models on social media data for sentiment analysis,
                                            image recognition, or recommendation systems.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h5 class="text-white">Academic Research</h5>
                                        <p class="text-muted small mb-0">
                                            Study social media behavior, content virality, and online communities
                                            with real data.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h5 class="text-white">Lead Generation</h5>
                                        <p class="text-muted small mb-0">
                                            Build prospect lists from business accounts with public contact information.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div class="col-12 col-md-6 mb-4">
                                <div class="card bg-dark border-0 h-100">
                                    <div class="card-body">
                                        <h5 class="text-white">Trend Analysis</h5>
                                        <p class="text-muted small mb-0">
                                            Track hashtag growth, content trends, and engagement patterns over time.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- NEXT STEPS -->
                    <div class="mt-5 pt-5 border-top border-secondary">
                        <h4>Next Steps</h4>
                        <div class="row mt-4">
                            <div class="col-12 col-md-6 mb-3">
                                <router-link :to="{path: '/datasets'}" class="card bg-primary border-0 h-100 text-decoration-none lift">
                                    <div class="card-body">
                                        <h6 class="text-white mb-1">Browse Datasets</h6>
                                        <p class="text-white-80 small mb-0">View all available datasets with pricing</p>
                                    </div>
                                </router-link>
                            </div>
                            <div class="col-12 col-md-6 mb-3">
                                <router-link :to="{path: '/docs/dataset-builder'}" class="card bg-dark border-0 h-100 text-decoration-none lift">
                                    <div class="card-body">
                                        <h6 class="text-white mb-1">Dataset Builder</h6>
                                        <p class="text-muted small mb-0">Create custom datasets with filters</p>
                                    </div>
                                </router-link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</div>
</template>

<script>
export default {
    created() {
        this.$methods.buildTitle('Datasets Documentation')
    },
    mounted() {
        if (this.$route.hash) {
            this.$nextTick(() => {
                const element = document.querySelector(this.$route.hash)
                if (element) {
                    element.scrollIntoView({ behavior: 'smooth' })
                }
            })
        }
    }
}
</script>
